import React from 'react';
import {
  GraduationCap, CalendarCheck, Wallet, Users, HeartPulse, ChevronRight,
} from 'lucide-react';
import type { Student } from '../../types';
import type { ProfileSnapshot } from '../StudentProfile';
import type { ProfileTabKey } from '../profile.types';
import { titleCase } from '@/utils/text';

interface Props {
  student: Student;
  snap: ProfileSnapshot;
  onGoTab: (t: ProfileTabKey) => void;
}

const OverviewTab: React.FC<Props> = ({ student, snap, onGoTab }) => {
  // Prefer the last-3-months summary for the Overview attendance card.
  const att3 = snap.attendanceSummary?.summary;
  const monthly = snap.attendanceSummary?.monthly ?? [];

  // Today's attendance status (from the current-month records already loaded).
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const todayStatus = (snap.attendance?.records ?? []).find((r) => String(r.date).slice(0, 10) === todayStr)?.status ?? null;
  const TODAY_BADGE: Record<string, string> = { present: 'b-success', absent: 'b-danger', late: 'b-warning' };
  const feeSum = snap.fees?.summary;
  const guardians = student.contacts ?? [];
  const primaryGuardian = guardians.find((c) => c.is_primary_guardian) ?? guardians[0];

  // Latest term result
  const terms = snap.reportCard?.terms ?? [];
  const latestTerm = [...terms].reverse().find((t) => t.summary.percent != null);

  const medical = student.medicalInfo;
  const hasMedicalFlags = !!(medical && (medical.allergies || medical.medical_conditions || medical.medications));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
      <Card title="Academics" icon={GraduationCap} onOpen={() => onGoTab('academics')}>
        {latestTerm ? (
          <>
            <Big value={`${latestTerm.summary.percent}%`} sub={`${latestTerm.name} average`} />
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-2)' }}>
              {latestTerm.exams.length} exam{latestTerm.exams.length !== 1 ? 's' : ''} recorded
            </div>
          </>
        ) : <Empty text={snap.loading ? 'Loading…' : 'No results recorded yet'} />}
      </Card>

      <Card
        title="Attendance"
        icon={CalendarCheck}
        onOpen={() => onGoTab('attendance')}
        headerRight={
          <span className={`badge ${todayStatus ? TODAY_BADGE[todayStatus] : 'b-neutral'}`} title="Today's status">
            <span className="dot" />
            {todayStatus ? `Today: ${titleCase(todayStatus)}` : 'Today: —'}
          </span>
        }
      >
        {att3 && att3.total_marked > 0 ? (
          <>
            <Big value={att3.percentage != null ? `${att3.percentage}%` : '—'} sub="last 3 months" />
            {monthly.length > 0 && (
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                {monthly.map((m) => (
                  <div key={m.month} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                      {m.percentage != null ? `${m.percentage}%` : '—'}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>{monthShort(m.month)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : <Empty text={snap.loading ? 'Loading…' : 'No attendance marked in the last 3 months'} />}
      </Card>

      <Card title="Fees" icon={Wallet} onOpen={() => onGoTab('fees')}>
        {feeSum ? (
          <>
            <Big
              value={`₹${feeSum.total_pending.toLocaleString('en-IN')}`}
              sub="pending"
              color={feeSum.total_pending > 0 ? 'var(--danger)' : 'var(--success)'}
            />
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-2)' }}>
              Paid ₹{feeSum.total_paid.toLocaleString('en-IN')} of ₹{feeSum.total_due.toLocaleString('en-IN')}
            </div>
          </>
        ) : <Empty text={snap.loading ? 'Loading…' : 'No fees assigned'} />}
      </Card>

      <Card title="Guardian" icon={Users} onOpen={() => onGoTab('profile')}>
        {primaryGuardian ? (
          <>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{titleCase(primaryGuardian.full_name)}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>
              {titleCase(primaryGuardian.relation_type)}
            </div>
            {primaryGuardian.phone_primary && (
              <div style={{ fontSize: 13, marginTop: 8 }}>{primaryGuardian.phone_primary}</div>
            )}
            {guardians.length > 1 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 8 }}>
                +{guardians.length - 1} more contact{guardians.length - 1 !== 1 ? 's' : ''}
              </div>
            )}
          </>
        ) : <Empty text="No guardian added" />}
      </Card>

      <Card title="Medical" icon={HeartPulse} onOpen={() => onGoTab('profile')}>
        {hasMedicalFlags ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {medical!.allergies && <Flag label="Allergies" value={medical!.allergies} />}
            {medical!.medical_conditions && <Flag label="Conditions" value={medical!.medical_conditions} />}
            {medical!.medications && <Flag label="Medications" value={medical!.medications} />}
          </div>
        ) : <Empty text="No medical flags" />}
      </Card>

      <Card title="Documents" icon={GraduationCap} onOpen={() => onGoTab('profile')}>
        <Big value={String(student.documents?.length ?? 0)} sub="files on record" />
      </Card>
    </div>
  );
};

function Card({ title, icon: Icon, onOpen, headerRight, children }: {
  title: string; icon: React.ElementType; onOpen: () => void;
  headerRight?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon size={16} style={{ color: 'var(--text-2)' }} />
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.2px' }}>{title}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {headerRight}
          <button className="icon-btn" style={{ width: 26, height: 26 }} onClick={onOpen} title={`Open ${title}`}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function Big({ value, sub, color }: { value: string; sub: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-1px', color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 5 }}>{sub}</div>
    </div>
  );
}

function Flag({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span style={{ fontWeight: 700, color: 'var(--text-2)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}: </span>
      <span>{value}</span>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ fontSize: 13, color: 'var(--text-3)', paddingTop: 4 }}>{text}</div>;
}

// "2026-07" → "Jul"
function monthShort(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-IN', { month: 'short' });
}

export default OverviewTab;
